<template>
  <div class="table-wrap" :style="scaleStyle">
    <my-loader v-if="isLoading && displayedOrders.length === 0" />
    <div v-else class="table-scroll">
      <table class="pb-table" v-if="displayedOrders.length > 0" :style="{ width: `${table.getTotalSize()}px` }">
        <thead>
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <th v-for="header in headerGroup.headers" :key="header.id" :style="{ width: `${header.getSize()}px` }">
              <button v-if="header.column.getCanSort()" class="th-sort" @click="sortBy(header.column.id, $event)">
                {{ header.column.columnDef.header }} {{ sortLabel(header.column.id) }}
              </button>
              <span v-else>{{ header.column.columnDef.header }}</span>
              <span
                class="column-resizer"
                :class="{ resizing: header.column.getIsResizing() }"
                title="Изменить ширину столбца"
                aria-hidden="true"
                @mousedown.stop="header.getResizeHandler()($event)"
                @touchstart.stop="header.getResizeHandler()($event)"
                @click.stop
                @dblclick.stop="header.column.resetSize()"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, idx) in table.getRowModel().rows" :key="row.id">
            <tr :class="rowClasses(row.original, idx)" @click="toggleExpand(row.id)">
              <td v-for="cell in row.getVisibleCells()" :key="cell.id" :style="{ width: `${cell.column.getSize()}px` }">
                <template v-if="cell.column.id === 'context.status'">
                  <span :class="['status-badge', statusClass(row.original)]">{{ statusText(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'uid'">
                  <span class="id-cell" :title="row.original.uid">{{ shortUid(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'context.create_dt'">{{ formatDate(row.original.context?.create_dt) }}</template>
                <template v-else-if="cell.column.id === 'context.end_dt'">{{ formatDate(row.original.context?.end_dt) }}</template>
                <template v-else-if="cell.column.id === 'route.departure.city_name'">
                  <span :title="cityName(getLoadingPoint(row.original))">{{ cityName(getLoadingPoint(row.original)) }}</span>
                  <span v-if="extraLoading(row.original) > 0" class="extra-count"> ({{ extraLoading(row.original) }})</span>
                </template>
                <template v-else-if="cell.column.id === 'route.destination.city_name'">
                  <span :title="cityName(getDestPoint(row.original))">{{ cityName(getDestPoint(row.original)) }}</span>
                  <span v-if="extraUnloading(row.original) > 0" class="extra-count"> ({{ extraUnloading(row.original) }})</span>
                </template>
                <template v-else-if="cell.column.id === 'cargo'"><span :title="cargoList(row.original)">{{ cargoList(row.original) }}</span></template>
                <template v-else-if="cell.column.id === 'veh.type'">{{ row.original.veh?.type || '—' }}</template>
                <template v-else-if="cell.column.id === 'cost.start'">{{ startCost(row.original) }}</template>
                <template v-else-if="cell.column.id === 'cost.last'">
                  <span :class="{ 'cost-bot': isFromBot(row.original) }">{{ lastCost(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'winner'">
                  <span :class="['winner-badge', winnerClass(row.original)]">{{ winnerText(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'meta.service_name'">{{ row.original.meta?.service_name || '—' }}</template>
                <template v-else-if="cell.column.id === 'meta.client_name'">{{ row.original.meta?.client_name || '—' }}</template>
                <template v-else-if="cell.column.id === 'task'">
                  <span v-if="taskState(row.original) === 'active'" class="task-badge">{{ taskValue(row.original) }}</span>
                  <span v-else-if="taskState(row.original) === 'inactive'" class="task-badge task-inactive" title="Неактивная задача">{{ taskValue(row.original) }}</span>
                  <span v-else>—</span>
                </template>
              </td>
            </tr>
            <tr v-show="expanded === row.id" class="detail-row">
              <td :colspan="row.getVisibleCells().length" :class="detailHighlightClass(idx)">
                <PureBiddingDetails :order="row.original" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-if="isRefreshing && displayedOrders.length > 0" class="refresh-bar" />

      <div v-if="displayedOrders.length === 0 && !isLoading" class="empty-table">
        Нет ордеров по заданным фильтрам
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { functionalUpdate, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import PureBiddingDetails from './PureBiddingDetails.vue';
import { tableScaleStyle } from '@/components/UI/tableScale.mjs';
import {
  EMPTY_VALUE,
  formatCargo,
  formatDate,
  formatMoney,
  getDestinationPoint,
  getExtraLoadingCount,
  getExtraUnloadingCount,
  getHighlightIndexes,
  getLoadingPoint,
  getStatusMeta,
  getCostMeta,
  getTaskMeta,
  getWinnerMeta,
  nextSorts,
  sortMeta,
  titleCase,
} from '../helpers';

export default {
  name: 'PureBiddingTable',
  components: { PureBiddingDetails },
  props: {
    orders: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    isRefreshing: { type: Boolean, default: false },
    palette: { type: String, default: 'colorful' },
    highlightMode: { type: String, default: 'none' },
    columnOrder: { type: Array, default: () => [] },
    columnVisibility: { type: Object, default: () => ({}) },
    columnSizing: { type: Object, default: () => ({}) },
    scale: { type: String, default: 'normal' },
  },
  emits: ['columnSizingChanged'],
  setup(props, { emit }) {
    const store = useStore();
    const expanded = ref(null);
    const sorts = computed(() => store.state.pureBidding.sorts || []);
    const displayedOrders = computed(() => (Array.isArray(props.orders) ? props.orders : []));
    const scaleStyle = computed(() => tableScaleStyle(props.scale));

    const columns = [
      { id: 'context.status', header: 'Статус', size: 90, minSize: 70, maxSize: 150 },
      { id: 'uid', header: 'UID', size: 120, minSize: 90, maxSize: 220, enableSorting: false },
      { id: 'context.create_dt', header: 'Создан', accessorFn: (o) => o.context?.create_dt, size: 85, minSize: 75, maxSize: 170 },
      { id: 'context.end_dt', header: 'Окончание', accessorFn: (o) => o.context?.end_dt, size: 85, minSize: 75, maxSize: 170, enableSorting: false },
      { id: 'route.departure.city_name', header: 'Откуда', accessorFn: (o) => getLoadingPoint(o)?.city_name, size: 120, minSize: 80, maxSize: 320 },
      { id: 'route.destination.city_name', header: 'Куда', accessorFn: (o) => getDestinationPoint(o)?.city_name, size: 120, minSize: 80, maxSize: 320 },
      { id: 'cargo', header: 'Груз', size: 100, minSize: 70, maxSize: 320, enableSorting: false },
      { id: 'veh.type', header: 'ТС', accessorFn: (o) => o.veh?.type, size: 45, minSize: 40, maxSize: 120, enableSorting: false },
      { id: 'cost.start', header: 'Старт цена', size: 90, minSize: 80, maxSize: 170, enableSorting: false },
      { id: 'cost.last', header: 'Последняя', size: 90, minSize: 80, maxSize: 170, enableSorting: false },
      { id: 'winner', header: 'Победитель', size: 110, minSize: 90, maxSize: 200, enableSorting: false },
      { id: 'meta.service_name', header: 'Платформа', accessorFn: (o) => o.meta?.service_name, size: 100, minSize: 80, maxSize: 200 },
      { id: 'meta.client_name', header: 'Клиент', accessorFn: (o) => o.meta?.client_name, size: 90, minSize: 70, maxSize: 200 },
      { id: 'task', header: 'Задача', size: 70, minSize: 60, maxSize: 160, enableSorting: false },
    ];

    const tanstackSorting = computed(() => sorts.value.map((s) => ({ id: s.field, desc: s.dir === -1 })));

    const table = useVueTable({
      get data() { return displayedOrders.value; },
      columns,
      getCoreRowModel: getCoreRowModel(),
      getRowId: (row, index) => row.uid || `${row.meta?.id}-${index}`,
      manualSorting: true,
      manualPagination: true,
      columnResizeMode: 'onChange',
      state: {
        get sorting() { return tanstackSorting.value; },
        get columnOrder() { return props.columnOrder; },
        get columnVisibility() { return props.columnVisibility; },
        get columnSizing() { return props.columnSizing; },
        get pagination() { return { pageIndex: 0, pageSize: 100 }; },
      },
      onSortingChange: () => {},
      onColumnSizingChange: (updater) => {
        emit('columnSizingChanged', functionalUpdate(updater, props.columnSizing));
      },
    });

    const toggleExpand = (key) => { expanded.value = expanded.value === key ? null : key; };
    const cityName = (point) => titleCase(point?.city_name);
    const cargoList = (order) => formatCargo(order);
    const extraLoading = (order) => getExtraLoadingCount(order);
    const extraUnloading = (order) => getExtraUnloadingCount(order);

    const statusText = (order) => getStatusMeta(order).label;
    const statusClass = (order) => {
      const s = getStatusMeta(order).value;
      return `status-${s?.toLowerCase() || 'unknown'}`;
    };

    const startCost = (order) => {
      const cost = getCostMeta(order);
      return cost.startWithoutNds != null ? formatMoney(cost.startWithoutNds) : EMPTY_VALUE;
    };
    const lastCost = (order) => {
      const cost = getCostMeta(order);
      return cost.lastWithoutNds != null ? formatMoney(cost.lastWithoutNds) : EMPTY_VALUE;
    };
    const isFromBot = (order) => order?.cost?.last?.from_bot === true;
    const winnerText = (order) => getWinnerMeta(order).text;
    const winnerClass = (order) => getWinnerMeta(order).className;

    const taskState = (order) => getTaskMeta(order).state;
    const taskValue = (order) => getTaskMeta(order).text;

    const shortUid = (order) => {
      const uid = order.uid;
      if (!uid) return EMPTY_VALUE;
      const parts = uid.split(':');
      return parts.length >= 2 ? `${parts[0].slice(-6)}:${parts[1]}` : uid;
    };

    const highlightIndexes = computed(() => getHighlightIndexes(displayedOrders.value, props.highlightMode));
    const rowClasses = (order, index) => {
      const classes = ['data-row'];
      const highlightIndex = highlightIndexes.value[index];
      if (props.highlightMode === 'status') {
        classes.push(`row-status-${(getStatusMeta(order).value || 'unknown').toLowerCase()}`);
      } else if (highlightIndex !== null) {
        classes.push(props.palette === 'zebra' ? `hl-zebra-${highlightIndex % 2}` : props.palette === 'bee' ? `hl-bee-${highlightIndex % 2}` : `hl-${highlightIndex % 12}`);
      }
      return classes;
    };
    const detailHighlightClass = (index) => {
      const order = displayedOrders.value[index];
      if (props.highlightMode === 'status') return `row-status-${(getStatusMeta(order).value || 'unknown').toLowerCase()}`;
      const highlightIndex = highlightIndexes.value[index];
      if (highlightIndex === null) return '';
      return props.palette === 'zebra' ? `hl-zebra-${highlightIndex % 2}` : props.palette === 'bee' ? `hl-bee-${highlightIndex % 2}` : `hl-${highlightIndex % 12}`;
    };

    const sortBy = (field, event) => store.dispatch('pureBidding/setSorts', nextSorts(sorts.value, field, event.shiftKey));
    const sortLabel = (field) => sortMeta(sorts.value, field).label;

    return {
      expanded,
      displayedOrders,
      scaleStyle,
      table,
      toggleExpand,
      formatDate,
      shortUid,
      getLoadingPoint,
      getDestPoint: getDestinationPoint,
      cityName,
      cargoList,
      extraLoading,
      extraUnloading,
      statusText,
      statusClass,
      startCost,
      lastCost,
      isFromBot,
      winnerText,
      winnerClass,
      taskState,
      taskValue,
      rowClasses,
      detailHighlightClass,
      sortBy,
      sortLabel,
    };
  },
};
</script>

<style scoped>
.table-wrap {
  position: relative;
  font-size: 13px;
  color: #333;
}
.table-scroll { overflow-x: auto; }

.pb-table {
  width: 100%;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.pb-table th,
.pb-table td {
  box-sizing: border-box;
  text-align: left;
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
  border-bottom: 1px solid #edf0f3;
  font-size: var(--table-cell-font-size);
  height: var(--table-cell-height);
  line-height: var(--table-cell-line-height);
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pb-table th {
  position: relative;
  font-weight: 700;
  background: #f7f8fa;
  color: #444;
}

.column-resizer {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  width: 10px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
}
.column-resizer::before {
  content: '';
  position: absolute;
  top: 4px;
  right: 4px;
  bottom: 4px;
  width: 1px;
  background: #aeb8c5;
  transition: background-color 120ms ease;
}
.column-resizer:hover::before,
.column-resizer.resizing::before {
  background: #007bff;
}

.th-sort {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.th-sort:hover { color: #007bff; }
.data-row { cursor: pointer; user-select: none; }
.data-row:hover { background: #f3f6fb; }

.status-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}

.status-play { background: #007bff; }
.status-won { background: #28a745; }
.status-lost { background: #dc3545; }
.status-not_participated { background: #6c757d; }
.status-annulled { background: #868e96; }
.status-canceled { background: #fd7e14; }
.status-deleted { background: #343a40; }
.status-unknown { background: #adb5bd; }
.status-done { background: #17a2b8; }
.status-tender { background: #6f42c1; }

.id-cell { font-weight: 600; }
.extra-count { color: #888; font-size: 12px; }
.cost-bot { color: #6f42c1; font-weight: 600; }

.winner-badge {
  display: inline-block;
  width: 96px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  text-align: left;
  box-sizing: border-box;
}

.winner-none { color: #999; padding: 0; width: 96px; }
.winner-bot { background: #ede9fe; color: #5b21b6; }
.winner-human { background: #e0f2fe; color: #075985; }
.winner-other { background: #f1f3f5; color: #495057; }
.winner-unknown { background: #fff3cd; color: #856404; }

.task-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  background: #fff3cd;
  color: #856404;
}

.task-inactive {
  background: #e9ecef;
  color: #868e96;
  font-weight: 600;
}

.detail-row,
.detail-row * { user-select: text; }
.detail-row td {
  padding: 0;
  border-bottom: 1px solid #edf0f3;
  white-space: normal;
  overflow: visible;
}

.empty-table {
  text-align: left;
  padding: 28px;
  color: #999;
  font-size: 14px;
}

.hl-0 { background: #eef6ff; --details-card-bg: #d9ebff; }
.hl-1 { background: #fff3f7; --details-card-bg: #f8dce7; }
.hl-2 { background: #effaf1; --details-card-bg: #d9f0de; }
.hl-3 { background: #fff7eb; --details-card-bg: #f6e4c8; }
.hl-4 { background: #f7f0fb; --details-card-bg: #eadcf3; }
.hl-5 { background: #edfafd; --details-card-bg: #d7edf2; }
.hl-6 { background: #fff0ec; --details-card-bg: #f3d9d1; }
.hl-7 { background: #f0f2ff; --details-card-bg: #dde2fb; }
.hl-8 { background: #fffbe8; --details-card-bg: #f2e8bd; }
.hl-9 { background: #f4f0ee; --details-card-bg: #e4dbd6; }
.hl-10 { background: #f5faed; --details-card-bg: #e1edcf; }
.hl-11 { background: #f3f0fb; --details-card-bg: #e3dcf3; }
.hl-zebra-0 { background: #fff; --details-card-bg: #edf0f3; }
.hl-zebra-1 { background: #f7f8fa; --details-card-bg: #e8ebef; }
.hl-bee-0 { background: #fff; --details-card-bg: #edf0f3; }
.hl-bee-1 { background: #fff8df; --details-card-bg: #f0e2a8; }

.row-status-play { background: #e8f0fe; --details-card-bg: #d0e1fd; }
.row-status-won { background: #d4edda; --details-card-bg: #bfe2c8; }
.row-status-lost { background: #ffe8e8; --details-card-bg: #f8d5d5; }
.row-status-not_participated { background: #f1f3f5; --details-card-bg: #e0e4e8; }
.row-status-canceled { background: #fff3e0; --details-card-bg: #f0d9a8; }

.refresh-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  overflow: hidden;
  background: transparent;
}

.refresh-bar::after {
  content: '';
  display: block;
  width: 35%;
  height: 100%;
  background: #007bff;
  animation: refresh-slide 1s linear infinite;
}

@keyframes refresh-slide {
  from { transform: translateX(-100%); }
  to { transform: translateX(300%); }
}
</style>
